import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { inquiryService } from '../services/inquiryService';
import { contactService } from '../services/contactService';
import { Phone, Mail, MapPin, Send } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { useError } from '../context/ErrorContext';
import { Helmet } from 'react-helmet-async';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(true);
  const [contactContent, setContactContent] = useState(null);
  const { showError } = useError();

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const data = await contactService.getContactContent();
        setContactContent(data);
      } catch (error) {
        showError('Failed to load contact page content: ' + error.message, 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [showError]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      toast.loading('Sending your message...', { id: 'contact-toast' });

      await inquiryService.createContactInquiry({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: `${formData.subject}\n\n${formData.message}`
      });

      toast.success('Message sent successfully!', { id: 'contact-toast' });
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message. Please try again.', { id: 'contact-toast' });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value
    }));
  };

  const scrollToContactForm = () => {
    const formElement = document.getElementById('send-message-form');
    if (formElement) {
      formElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  if (loading) {
    return (
       <div className="flex justify-center items-center min-h-[400px]">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent"></div>
          </div>
    );
  }

  if (!contactContent) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50">
        <p className="text-lg text-red-600">Failed to load Contact page content. Please try again later.</p>
      </div>
    );
  }

  const { main_heading, main_subheading, contact_info, map_embed_url, cta_heading, cta_subheading } = contactContent;

  const contactDetails = [
    { icon: Phone, title: "Call Us", info: contact_info.phone, action: `tel:${contact_info.phone.replace(/\s/g, '')}` },
    { icon: Mail, title: "Email Us", info: contact_info.email, action: `mailto:${contact_info.email}` },
    { icon: MapPin, title: "Our Office", info: contact_info.address, action: contact_info.address_url }
  ];

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 font-sans text-gray-800">
      <Helmet>
        <title>Contact Us | Antacres</title>
        <meta 
          name="description" 
          content="Get in touch with us. Send us a message, find our office location on the map, or contact us by phone or email. We are here to help you." 
        />
      </Helmet>
      <Toaster position="bottom-center" richColors />

      <div className="relative h-[40vh] bg-gradient-to-br from-accent to-black/80 overflow-hidden flex items-center justify-center text-white">
        <div className="absolute inset-0 z-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
        <div className="relative z-10 text-center p-4">
          <motion.h1
            initial={{ opacity: 0, y: -30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ amount: 0.2 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="text-4xl md:text-6xl font-extrabold tracking-tight mt-10 drop-shadow-lg"
          >
            {main_heading}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ amount: 0.2 }}
            transition={{ delay: 0.3, duration: 0.8, ease: "easeOut" }}
            className="text-lg md:text-xl max-w-2xl mx-auto opacity-90 drop-shadow"
          >
            {main_subheading}
          </motion.p>
        </div>
      </div>
      
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 lg:px-8 max-w-5xl">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ amount: 0.2 }}
            className="flex flex-col md:flex-row justify-between items-start md:items-center mb-16"
          >
            {contactDetails.map((item, index) => (
              <motion.a
                href={item.action}
                target={item.action.startsWith('http') ? "_blank" : "_self"}
                rel={item.action.startsWith('http') ? "noopener noreferrer" : ""}
                key={item.title}
                variants={fadeInUp}
                className="flex items-center gap-4 py-4 w-full md:w-auto hover:text-accent transition-colors duration-300 group"
              >
                <div className="w-12 h-12 flex-shrink-0 bg-accent/10 rounded-full flex items-center justify-center text-accent group-hover:bg-accent group-hover:text-white transition-all duration-300">
                  <item.icon size={24} strokeWidth={1.8} />
                </div>
                <div className="flex flex-col">
                  <h3 className="text-lg font-bold text-gray-900 group-hover:text-accent transition-colors duration-300">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed group-hover:text-gray-800 transition-colors duration-300">{item.info}</p>
                </div>
              </motion.a>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="bg-white py-16 md:py-24">
        <div className="container mx-auto px-4 lg:px-8">
              {/* Contact Form */}
              <motion.div
                id="send-message-form"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ amount: 0.2 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="w-full max-w-xl mx-auto mb-24"
              >
                <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Send us a Message</h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your Name"
                      className="w-full px-5 py-3 border-b border-gray-300 focus:border-accent outline-none transition-all duration-200 text-lg"
                      required
                    />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Your Email"
                      className="w-full px-5 py-3 border-b border-gray-300 focus:border-accent outline-none transition-all duration-200 text-lg"
                      required
                    />
                  </div>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Mobile Number"
                    className="w-full px-5 py-3 border-b border-gray-300 focus:border-accent outline-none transition-all duration-200 text-lg"
                  />
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="Subject"
                    className="w-full px-5 py-3 border-b border-gray-300 focus:border-accent outline-none transition-all duration-200 text-lg"
                    required
                  />
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Your Message"
                    rows="6"
                    className="w-full px-5 py-3 border-b border-gray-300 focus:border-accent outline-none transition-all duration-200 text-lg"
                    required
                  ></textarea>
                  <motion.button
                    type="submit"
                    whileHover={{ scale: 1.02, boxShadow: "0 8px 16px rgba(0,0,0,0.2)" }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full bg-accent text-white font-semibold py-4 rounded-xl text-lg shadow-lg hover:bg-primary-700 transition-all duration-300 flex items-center justify-center gap-3"
                  >
                    <Send size={20} /> Send Message
                  </motion.button>
                </form>
              </motion.div>

              {/* Map Section */}
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ amount: 0.2 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="w-full"
              >
                <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Visit Our Office</h2>
                <div className="h-[500px] md:h-[600px] overflow-hidden">
                  <iframe
                    src={map_embed_url}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Office Location Map"
                  ></iframe>
                </div>
              </motion.div>
          </div>
      </section>

      {/* CTA Section */}
      <section className="bg-accent text-white py-16 md:py-20 text-center shadow-inner">
        <div className="container mx-auto px-4 lg:px-8 max-w-4xl">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ amount: 0.2 }}
            transition={{ duration: 0.6 }}
            className="text-3xl md:text-5xl font-bold mb-4 drop-shadow-xl leading-tight"
          >
            {cta_heading}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ amount: 0.2 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="text-lg md:text-2xl max-w-3xl mx-auto opacity-95 font-light"
          >
            {cta_subheading}
          </motion.p>
          <motion.button
            whileHover={{ scale: 1.05, boxShadow: "0 10px 30px rgba(255,255,255,0.2)" }}
            whileTap={{ scale: 0.95 }}
            className="mt-10 bg-white text-accent py-4 px-10 rounded-full font-bold text-lg md:text-xl shadow-lg hover:bg-gray-100 transition-all duration-300 ease-in-out transform hover:-translate-y-1 focus:outline-none focus:ring-4 focus:ring-accent focus:ring-offset-2"
            onClick={scrollToContactForm}
          >
            Get in Touch Today!
          </motion.button>
        </div>
      </section>
    </div>
  );
};

export default Contact;